import { PayloadAction, Draft, createAsyncThunk, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import {
	Position,
	RecipeWidget,
	WidgetType,
} from '@kemu-io/kemu-core/types';
import { LogicMapperState, SelectedItem, getSelectedWidgetIds } from '../logicMapperSlice';
import { addToKemuClipboard, getFromKemuClipboard } from './clipboardUtils';
import { initializeAddedWidgets } from './common/widgetInitialization';
import {
	getWidgetsInThing,
	getCustomWidgetInnerWidgets,
	addWidgetsFromStringMap,
} from '@src/app/recipe/utils';
import { RootState } from '@src/app/store';
import { safeJsonClone, widgetMapToStatelessMap } from '@common/utils';
import { WidgetsMap, StatelessWidgetsMap } from '@src/types/core_t';
import { getGlobalIntl } from '@src/assets/i18n/globalIntl';

type CopySelectedWidgetsAction = {
	/** id of the recipe in the pool */
	recipeId: string;
	/** id of the Thing in the recipe */
	thingId: string;
	groupId?: string;
};

/**
 * Copies all the widgets in the current selection to the clipboard
 */
const copySelectedWidgetsReducer = (state: Draft<LogicMapperState>, action: PayloadAction<CopySelectedWidgetsAction>): void => {

	const widgetsMap = getWidgetsInThing(action.payload.recipeId, action.payload.thingId);
	let widgetsToCloneMap: WidgetsMap = {};

	const selectedWidgetsIds: string[] = [];
	// Only process actual widgets, not virtual ports
	const selectedWidgetsOnly = getSelectedWidgetIds(state.selectedWidgets);

	selectedWidgetsOnly.forEach((widgetId) => {
		const widgetInThing = widgetsMap[widgetId];
			if (widgetInThing) {
			// Make a copy of the widget without their state's private properties
			const safeWidgetCopy = safeJsonClone<RecipeWidget>(widgetInThing);
			// Check if a bundle is selected, if so, make sure we restore 
			// bundle's processors private data to point to their cache location.
			

			widgetsToCloneMap[widgetInThing.id] = safeWidgetCopy;
			selectedWidgetsIds.push(widgetInThing.id);
		}
	});


	// find group type widgets in the selection and add to the list any inner widgets.
	Object.values(widgetsToCloneMap).forEach((widget) => {
		if (widget.type === WidgetType.widgetGroup) {
			const childrenMap = getCustomWidgetInnerWidgets(
				widget.id,
				action.payload.thingId,
				action.payload.recipeId,
				// We need to send the list of selected widgets so that outer children
				// are not removed from the list of children.
				selectedWidgetsIds
			);

			widgetsToCloneMap = {
				...widgetsToCloneMap,
				...childrenMap
			};

			// Update the original reference with the updated fields
			widgetsToCloneMap[widget.id].state = {
				...widget.state,
			};
		}
	});

	// IMPORTANT: Remove children references that are not part of the selection
	// this may happen if the selected widget is linked to a root level widget
	// that is not part of the selection.
	for (const widgetId in widgetsToCloneMap) {
		const widget = widgetsToCloneMap[widgetId];
		widget.children = (widget.children ||[]).filter((child) => {
			return !!widgetsToCloneMap[child.childId];
		});
	}

	// Remove the groupId from any widget at the root level
	if (action.payload.groupId) {
		Object.values(widgetsToCloneMap).forEach(widget => {
			if (widget.groupId === action.payload.groupId) {
				widget.groupId = undefined;
			}
		});
	}

	addToKemuClipboard(widgetsToCloneMap);
};

/**
 * Keeps track of all selected widgets 
 */
const setSelectedWidgetsReducer = (state: Draft<LogicMapperState>, action: PayloadAction<SelectedItem[]>): void => {
	state.selectedWidgets = action.payload;
};

/**
 * Checks the clipboard for a valid kemu-structure string and adds the list of widgets to the current thing.
 */
export const pasteFromClipboardAction = createAsyncThunk('/clipboard/pasteFromClipboardAction', async (
	payload: {
		recipeId: string,
		thingId: string,
		mouseLocation?: Position,
		/** 
		 * A stringified map of widgets to be added. If not provided, it attempts to read 
		 * and decode text from the actual system clipboard.
		 */
		mapData?: string,
	},
	thunkApi
): Promise<StatelessWidgetsMap> => {
	const clipboardData = payload.mapData || await getFromKemuClipboard();
	if (clipboardData) {
		const { logicMapper, workspace, user } = thunkApi.getState() as RootState;

		const mousePosition = payload.mouseLocation || logicMapper.lastMouseClick;
		const zoom = logicMapper.canvasZoom;

		const groupId = workspace.folderPath[workspace.folderPath.length - 1]?.groupId;
		const t = getGlobalIntl();

		// mousePosition.x = mousePosition.x / zoom;
		// mousePosition.y = mousePosition.y / zoom;
		// console.log(`Pasting from clipboard at ${mousePosition.x}, ${mousePosition.y}, zoom: ${zoom}`);

		const { addedWidgets } = await addWidgetsFromStringMap(
			clipboardData, payload.recipeId, payload.thingId, mousePosition, zoom, groupId
		);

		const addedWidgetsList = Object.values(addedWidgets);

		await initializeAddedWidgets({
			recipeId: payload.recipeId,
			thingId: payload.thingId,
			addedWidgetsList,
			addedWidgetsMap: addedWidgets,
			thunkApi,
			userId: user.currentUserProfile.profile?.id
		});

		const statelessMap = widgetMapToStatelessMap(addedWidgets);

		return statelessMap;
	}

	return {};
});


export const pasteFromClipboardReducer = ((builder: ActionReducerMapBuilder<LogicMapperState>): void => {
	builder.addCase(pasteFromClipboardAction.fulfilled, (state, action: PayloadAction<StatelessWidgetsMap>) => {
		state.gates = {
			...state.gates,
			...action.payload
		};

		// Also force re-rendering
		state.renderCounter = state.renderCounter + 1;
	});

	builder.addCase(pasteFromClipboardAction.rejected, (state, action) => {
		console.error('Failed to paste from clipboard: ', action.error);
	});
});

export default {
	copySelectedWidgetsReducer,
	setSelectedWidgetsReducer,
};
